/**
 * Enhanced API Response Handler
 *
 * This utility provides a comprehensive way to handle API responses with:
 * - Smart response parsing for multiple formats
 * - Retry mechanism for transient failures
 * - Response validation with detailed error reporting
 * - Entity extraction for nested structures
 * - Request caching for GET requests
 * - Circuit breaker for failing endpoints
 * - Timeout handling
 *
 * Backend API Standard:
 * {
 *   success: boolean,
 *   data: any,
 *   message?: string,
 *   meta?: {
 *     page?: number,
 *     limit?: number,
 *     total?: number,
 *     totalPages?: number
 *   }
 * }
 *
 * Usage:
 * ```typescript
 * const response = await apiCall();
 * const result = APIResponseHandler.handle(response);
 *
 * if (result.success) {
 *   const data = result.data;
 *   // Handle success
 * } else {
 *   throw new Error(result.message);
 * }
 * ```
 */

// ============================================================================
// TYPES
// ============================================================================

export interface APIResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
    [key: string]: any;
  };
  _raw?: any; // Include raw response for debugging
}

export interface APIResponseWithPagination<T = any> extends APIResponse<T[]> {
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    [key: string]: any;
  };
}

export interface RetryConfig {
  maxAttempts?: number;
  delayMs?: number;
  backoffMultiplier?: number;
  maxDelayMs?: number;
  retryableErrors?: string[];
}

export interface ValidationOptions {
  requireSuccess?: boolean;
  requireData?: boolean;
  allowedDataTypes?: string[];
  customValidators?: Array<(data: any) => { valid: boolean; error?: string }>;
}

export interface CircuitBreakerState {
  isOpen: boolean;
  failureCount: number;
  lastFailureTime: number;
  nextAttemptTime: number;
}

// ============================================================================
// CACHE & CIRCUIT BREAKER
// ============================================================================

interface CacheEntry {
  data: any;
  timestamp: number;
  expiresAt: number;
}

class RequestCache {
  private cache = new Map<string, CacheEntry>();
  private defaultTTL = 5 * 60 * 1000; // 5 minutes

  set(key: string, data: any, ttlMs?: number): void {
    const now = Date.now();
    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt: now + (ttlMs || this.defaultTTL),
    });
  }

  get(key: string): any | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  clear(): void {
    this.cache.clear();
  }

  clearPattern(pattern: string): void {
    const regex = new RegExp(pattern);
    const keys = Array.from(this.cache.keys());
    for (const key of keys) {
      if (regex.test(key)) {
        this.cache.delete(key);
      }
    }
  }
}

class CircuitBreaker {
  private states = new Map<string, CircuitBreakerState>();
  private threshold = 5; // failures before opening
  private resetTimeoutMs = 60 * 1000; // 1 minute

  canExecute(endpoint: string): boolean {
    const state = this.states.get(endpoint);
    if (!state || !state.isOpen) return true;

    if (Date.now() >= state.nextAttemptTime) {
      // Reset circuit breaker
      this.states.delete(endpoint);
      return true;
    }

    return false;
  }

  recordSuccess(endpoint: string): void {
    this.states.delete(endpoint);
  }

  recordFailure(endpoint: string): void {
    const state = this.states.get(endpoint) || {
      isOpen: false,
      failureCount: 0,
      lastFailureTime: 0,
      nextAttemptTime: 0,
    };

    state.failureCount++;
    state.lastFailureTime = Date.now();

    if (state.failureCount >= this.threshold) {
      state.isOpen = true;
      state.nextAttemptTime = Date.now() + this.resetTimeoutMs;
      console.warn(`[APIResponseHandler] Circuit breaker opened for ${endpoint}`);
    }

    this.states.set(endpoint, state);
  }

  reset(endpoint: string): void {
    this.states.delete(endpoint);
  }

  clear(): void {
    this.states.clear();
  }
}

// ============================================================================
// MAIN HANDLER CLASS
// ============================================================================

export class APIResponseHandler {
  private static cache = new RequestCache();
  private static circuitBreaker = new CircuitBreaker();
  private static defaultRetryConfig: RetryConfig = {
    maxAttempts: 3,
    delayMs: 1000,
    backoffMultiplier: 2,
    maxDelayMs: 10000,
    retryableErrors: ['NETWORK_ERROR', 'TIMEOUT', 'ECONNRESET'],
  };

  // ==========================================================================
  // RESPONSE TRANSFORMATION
  // ==========================================================================

  /**
   * Transform MongoDB _id to id property
   * Recursively processes objects and arrays
   */
  static transformMongoId<T>(data: any): T {
    if (data === null || data === undefined) {
      return data;
    }

    // Handle arrays
    if (Array.isArray(data)) {
      return data.map(item => this.transformMongoId(item)) as any;
    }

    // Handle objects (but not Date, RegExp, etc.)
    if (typeof data === 'object' && !(data instanceof Date) && !(data instanceof RegExp)) {
      const transformed: any = { ...data };

      // Transform _id to id (only if id doesn't already exist)
      if (transformed._id && !transformed.id) {
        transformed.id = transformed._id;
      }

      // Recursively transform nested objects
      for (const key in transformed) {
        if (transformed[key] && typeof transformed[key] === 'object') {
          transformed[key] = this.transformMongoId(transformed[key]);
        }
      }

      return transformed;
    }

    return data;
  }

  /**
   * Extract entity from nested response structure
   * Handles: { data: { order: {...} } } -> returns order
   */
  static extractEntity<T>(response: any, entityKey?: string): APIResponse<T> {
    const handled = this.handleWithFallback(response);

    // If specific entity key requested, extract it
    if (entityKey && handled.data && typeof handled.data === 'object') {
      const entityData = (handled.data as any)[entityKey];
      if (entityData !== undefined) {
        return {
          ...handled,
          data: this.transformMongoId(entityData),
        };
      }
    }

    // Auto-detect common entity keys
    const commonKeys = ['order', 'product', 'user', 'customer', 'driver', 'subscription',
                        'category', 'subcategory', 'subcategories', 'coupon', 'banner', 'deliveryTimeSlot',
                        'careGuide', 'greetingCard', 'inventory', 'warehouse'];

    if (handled.data && typeof handled.data === 'object') {
      for (const key of commonKeys) {
        if (key in (handled.data as any) && key !== 'data' && key !== 'meta') {
          return {
            ...handled,
            data: this.transformMongoId((handled.data as any)[key]),
          };
        }
      }
    }

    return handled as APIResponse<T>;
  }

  // ==========================================================================
  // RESPONSE PARSING (Enhanced)
  // ==========================================================================

  /**
   * Handle a standard API response
   */
  static handle<T>(response: any): APIResponse<T> {
    const transformedData = this.transformMongoId<T>(response.data);
    return {
      success: response.success,
      data: transformedData,
      message: response.message,
      meta: response.meta,
    };
  }

  /**
   * Enhanced response handler with intelligent fallback
   * Handles ALL response patterns found in the wild:
   *
   * Pattern 1: { success, data, message } - Standard format
   * Pattern 2: { data: { success, data, message } } - Double-nested
   * Pattern 3: { data: { entity: {...} } } - Entity wrapper
   * Pattern 4: { data: [items] } - Array response
   * Pattern 5: AxiosResponse.data - Raw axios response
   * Pattern 6: { success: false, message: '...' } - Error response
   */
  static handleWithFallback<T>(response: any): APIResponse<T> {
    // Guard against null/undefined
    if (!response) {
      return {
        success: false,
        data: null as any,
        error: 'Response is null or undefined',
      };
    }

    // Pattern 6: Error response format: { success: false, message: '...' }
    if (typeof response.success === 'boolean' && response.success === false) {
      return {
        success: false,
        data: (response.data ?? null) as T,
        message: response.message,
        error: response.message,
      };
    }

    // Pattern 1: New format: { success, data, message, meta }
    if (typeof response.success === 'boolean' && 'data' in response) {
      const transformedData = this.transformMongoId<T>(response.data);
      return {
        success: response.success,
        data: transformedData,
        message: response.message,
        meta: response.meta,
      };
    }

    // Pattern 1b: Delete response format: { success: true, message: '...' } without data field
    // This handles delete operations that don't return data
    if (typeof response.success === 'boolean' && response.success === true && !('data' in response)) {
      return {
        success: true,
        data: null as T,
        message: response.message,
        meta: response.meta,
      };
    }

    // Pattern 2: Legacy format 1: { data: { success, data, message } }
    // This handles double-nested responses from axios
    if (response.data && typeof response.data.success === 'boolean') {
      const transformedData = this.transformMongoId<T>(response.data.data);
      return {
        success: response.data.success,
        data: transformedData,
        message: response.data.message,
        meta: response.data.meta,
      };
    }

    // Pattern 3: Triple-nested: { data: { data: { success, data } } }
    if (response.data && response.data.data && typeof response.data.data.success === 'boolean') {
      const transformedData = this.transformMongoId<T>(response.data.data.data);
      return {
        success: response.data.data.success,
        data: transformedData,
        message: response.data.data.message,
        meta: response.data.data.meta,
      };
    }

    // Pattern 4: Array response: { data: [items] }
    if (response.data && Array.isArray(response.data)) {
      const transformedData = this.transformMongoId<T>(response.data);
      return {
        success: true,
        data: transformedData,
        message: response.message,
        meta: response.meta,
      };
    }

    // Pattern 5: Data-only response: { data: {...} } without success flag
    if (response.data && typeof response.data === 'object') {
      const transformedData = this.transformMongoId<T>(response.data);
      return {
        success: true,
        data: transformedData,
        message: response.message,
        meta: response.meta,
      };
    }

    // Ultimate fallback: Assume success with direct data
    const transformedData = this.transformMongoId<T>(response.data || response);
    return {
      success: true,
      data: transformedData,
      message: response.message || '',
    };
  }

  // ==========================================================================
  // VALIDATION
  // ==========================================================================

  /**
   * Validate response with detailed error reporting
   */
  static validate(response: any, options?: ValidationOptions): {
    isValid: boolean;
    issues: string[];
    warnings: string[];
  } {
    const issues: string[] = [];
    const warnings: string[] = [];
    const opts = options || {};

    if (!response) {
      issues.push('Response is null or undefined');
      return { isValid: false, issues, warnings };
    }

    // Check success flag
    if (opts.requireSuccess !== false) {
      if ('success' in response) {
        if (typeof response.success !== 'boolean') {
          issues.push('success field must be boolean');
        } else if (!response.success) {
          issues.push(`Response indicates failure: ${response.message || 'No message'}`);
        }
      }
    }

    // Check data field
    if (opts.requireData) {
      if (!('data' in response)) {
        issues.push('data field is missing');
      } else if (response.data === null || response.data === undefined) {
        issues.push('data field is null or undefined');
      }
    }

    // Check data types
    if (opts.allowedDataTypes && response.data !== undefined) {
      const actualType = Array.isArray(response.data) ? 'array' : typeof response.data;
      if (!opts.allowedDataTypes.includes(actualType)) {
        issues.push(`data type is ${actualType}, expected one of: ${opts.allowedDataTypes.join(', ')}`);
      }
    }

    // Run custom validators
    if (opts.customValidators) {
      for (const validator of opts.customValidators) {
        const result = validator(response.data);
        if (!result.valid) {
          issues.push(result.error || 'Validation failed');
        }
      }
    }

    // Warnings (non-critical issues)
    if (!response.message && !response.success) {
      warnings.push('Error response missing message');
    }

    if (response.data === undefined && !('success' in response)) {
      warnings.push('Response has neither success flag nor data');
    }

    return {
      isValid: issues.length === 0,
      issues,
      warnings,
    };
  }

  // ==========================================================================
  // RETRY MECHANISM
  // ==========================================================================

  /**
   * Execute async function with retry logic
   */
  static async withRetry<T>(
    fn: () => Promise<T>,
    config?: RetryConfig
  ): Promise<T> {
    const cfg = { ...this.defaultRetryConfig, ...config };
    let lastError: Error;
    let delay = cfg.delayMs!;

    for (let attempt = 1; attempt <= cfg.maxAttempts!; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error as Error;

        // Check if error is retryable
        const isRetryable = cfg.retryableErrors?.some(pattern =>
          (error as Error).message.includes(pattern)
        ) ?? false;

        if (attempt === cfg.maxAttempts! || !isRetryable) {
          break;
        }

        console.warn(`[APIResponseHandler] Retry attempt ${attempt}/${cfg.maxAttempts}:`, (error as Error).message);
        await this.delay(delay);
        delay = Math.min(delay * (cfg.backoffMultiplier || 2), cfg.maxDelayMs!);
      }
    }

    throw lastError!;
  }

  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // ==========================================================================
  // CIRCUIT BREAKER
  // ==========================================================================

  /**
   * Execute with circuit breaker protection
   */
  static async withCircuitBreaker<T>(
    endpoint: string,
    fn: () => Promise<T>
  ): Promise<T> {
    if (!this.circuitBreaker.canExecute(endpoint)) {
      throw new Error(`Circuit breaker is open for ${endpoint}. Too many failures.`);
    }

    try {
      const result = await fn();
      this.circuitBreaker.recordSuccess(endpoint);
      return result;
    } catch (error) {
      this.circuitBreaker.recordFailure(endpoint);
      throw error;
    }
  }

  static resetCircuitBreaker(endpoint?: string): void {
    if (endpoint) {
      this.circuitBreaker.reset(endpoint);
    } else {
      this.circuitBreaker.clear();
    }
  }

  // ==========================================================================
  // CACHING
  // ==========================================================================

  /**
   * Generate cache key from URL and params
   */
  private static generateCacheKey(url: string, params?: any): string {
    const paramString = params ? JSON.stringify(params) : '';
    return `${url}:${paramString}`;
  }

  /**
   * Execute with caching (GET requests only)
   */
  static async withCache<T>(
    url: string,
    fn: () => Promise<T>,
    ttlMs?: number
  ): Promise<T> {
    const cacheKey = this.generateCacheKey(url);

    // Check cache
    const cached = this.cache.get(cacheKey);
    if (cached !== null) {
      return cached;
    }

    // Execute and cache
    const result = await fn();
    this.cache.set(cacheKey, result, ttlMs);
    return result;
  }

  static clearCache(pattern?: string): void {
    if (pattern) {
      this.cache.clearPattern(pattern);
    } else {
      this.cache.clear();
    }
  }

  // ==========================================================================
  // CONVENIENCE METHODS
  // ==========================================================================

  /**
   * Extract data from response, throwing error if unsuccessful
   */
  static getDataOrThrow<T>(response: any): T {
    const result = this.handleWithFallback(response);

    if (!result.success) {
      throw new Error(result.message || result.error || 'API request failed');
    }

    return result.data as T;
  }

  /**
   * Extract data from response, returning null if unsuccessful
   */
  static getDataOrNull<T>(response: any): T | null {
    try {
      return this.getDataOrThrow<T>(response);
    } catch {
      return null;
    }
  }

  /**
   * Extract entity from response with auto-detection
   */
  static getEntityOrThrow<T>(response: any, entityKey?: string): T {
    const result = this.extractEntity<T>(response, entityKey);

    if (!result.success) {
      throw new Error(result.message || result.error || 'API request failed');
    }

    return result.data;
  }

  /**
   * Check if response is successful
   */
  static isSuccessful(response: any): boolean {
    return response.success === true ||
           (response.data && response.data.success === true);
  }

  /**
   * Get success status from response
   */
  static getSuccessStatus(response: any): boolean {
    // Check new format first
    if ('success' in response) {
      return response.success;
    }

    // Check legacy format
    if (response.data && 'success' in response.data) {
      return response.data.success;
    }

    // Default to true for data-only responses
    return true;
  }

  /**
   * Get message from response
   */
  static getMessage(response: any): string | undefined {
    // Check new format first
    if ('message' in response && response.message) {
      return response.message;
    }

    // Check legacy format
    if (response.data && 'message' in response.data && response.data.message) {
      return response.data.message;
    }

    return undefined;
  }

  /**
   * Get error message from response
   */
  static getErrorMessage(response: any): string | undefined {
    return response.error || response.message || this.getMessage(response);
  }

  // ==========================================================================
  // BATCH OPERATIONS
  // ==========================================================================

  /**
   * Execute multiple requests in parallel and aggregate results
   */
  static async batch<T>(
    requests: Array<() => Promise<T>>,
    options?: { parallel?: number; stopOnError?: boolean }
  ): Promise<Array<{ success: boolean; data?: T; error?: string }>> {
    const opts = { parallel: 5, stopOnError: false, ...options };
    const results: Array<{ success: boolean; data?: T; error?: string }> = [];

    for (let i = 0; i < requests.length; i += opts.parallel!) {
      const batch = requests.slice(i, i + opts.parallel!);
      const batchResults = await Promise.allSettled(
        batch.map(fn => fn())
      );

      for (const result of batchResults) {
        if (result.status === 'fulfilled') {
          results.push({ success: true, data: result.value });
        } else {
          results.push({
            success: false,
            error: (result.reason as Error).message,
          });
          if (opts.stopOnError) {
            return results;
          }
        }
      }
    }

    return results;
  }

  // ==========================================================================
  // TIMEOUT HANDLING
  // ==========================================================================

  /**
   * Execute with timeout
   */
  static async withTimeout<T>(
    fn: () => Promise<T>,
    timeoutMs: number
  ): Promise<T> {
    return Promise.race([
      fn(),
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error(`Timeout after ${timeoutMs}ms`)), timeoutMs)
      ),
    ]);
  }
}

export default APIResponseHandler;
